/**
 * Slugs — the lower-case, hyphenated words an address is made of. One function, because
 * a blog post's slug (`.docs/46-blog-and-forms.md` §4) and the `id` on each of its
 * headings (`@fareast/rich-text` `headingsOf`) have to agree on what "Küçüktaş" becomes,
 * and a second copy would be the one that disagreed.
 *
 * ASCII only, on purpose: a slug is typed into a URL bar, pasted into a mail and quoted
 * on the phone. A title in a script with no Latin form — Cyrillic, Greek, Han — folds to
 * the empty string, and the caller decides what an empty slug means (a post falls back to
 * its reference, a heading to `section`).
 */
/** What a slug looks like: one or more runs of `[a-z0-9]`, joined by single hyphens. */
export const SLUG_PATTERN = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;
/** The longest slug `slugify` makes, and the longest a blog post may have. */
export const SLUG_MAX_LENGTH = 96;
/**
 * Letters Unicode does not decompose into a base letter and a mark, so NFKD leaves them
 * whole and the strip below would delete them. Lower case only: the input is lower-cased
 * first, which turns `ẞ` into `ß` and `İ` into `i` plus a combining dot.
 */
const FOLDS = {
    ß: 'ss',
    ı: 'i',
    ø: 'o',
    æ: 'ae',
    œ: 'oe',
    đ: 'd',
    ð: 'd',
    ł: 'l',
    þ: 'th',
};
/** Every character `FOLDS` has an answer for. */
const FOLDABLE = new RegExp(`[${Object.keys(FOLDS).join('')}]`, 'g');
/**
 * A slug made from free text: Unicode folded to ASCII ("Küçüktaş" → `kucuktas`, "ı" → `i`,
 * "ß" → `ss`), lower case, every run of anything else one hyphen, no hyphen at either end,
 * and at most `SLUG_MAX_LENGTH` characters — cut at a hyphen when there is one to cut at,
 * so a long title loses its last words rather than half of one.
 *
 * The result matches `SLUG_PATTERN` or is the empty string.
 */
export function slugify(text) {
    const folded = text
        .toLowerCase()
        .normalize('NFKD')
        .replace(/\p{M}+/gu, '')
        .replace(FOLDABLE, (letter) => FOLDS[letter] ?? '');
    const slug = folded.replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
    if (slug.length <= SLUG_MAX_LENGTH)
        return slug;
    const cut = slug.slice(0, SLUG_MAX_LENGTH);
    // The character after the cut is a hyphen: the cut fell between two words already.
    if (slug[SLUG_MAX_LENGTH] === '-')
        return cut;
    const lastHyphen = cut.lastIndexOf('-');
    // One word longer than the whole budget has no boundary to cut at.
    return lastHyphen > 0 ? cut.slice(0, lastHyphen) : cut;
}
/** Whether `value` is a slug: `SLUG_PATTERN`, and no longer than `SLUG_MAX_LENGTH`. */
export function isSlug(value) {
    return value.length <= SLUG_MAX_LENGTH && SLUG_PATTERN.test(value);
}
//# sourceMappingURL=slug.js.map